import { useState, useEffect, SyntheticEvent, lazy, Suspense } from 'react';
import { Box, Tabs, Tab, Typography, Stack, CircularProgress } from '@mui/material';
import { Notifications as BellIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { usePermission } from '@/hooks/usePermission';
import PageHelpIcon from '@/components/PageHelpIcon';

const NotificationPreferencesPage = lazy(() => import('./NotificationPreferencesPage'));
const NotificationTemplatesPage = lazy(() => import('./NotificationTemplatesPage'));
const EmailSettingsPage = lazy(() => import('./EmailSettingsPage'));

// Deep-link support: /settings/notifications#templates → Templates tab.
// Keeps legacy links from retired /settings/notification-templates +
// /settings/email working after the P1-5 consolidation redirect.
const TAB_BY_HASH: Record<string, number> = {
  '#preferences': 0,
  '#templates': 1,
  '#email-delivery': 2,
};
const HASH_BY_TAB = ['#preferences', '#templates', '#email-delivery'];

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
          {children}
        </Suspense>
      )}
    </Box>
  );
}

export default function NotificationsPage() {
  const [tab, setTab] = useState<number>(() => TAB_BY_HASH[window.location.hash] ?? 0);
  const { isSystemAdmin, isGroupAdmin } = usePermission();
  const isAdmin = isSystemAdmin || isGroupAdmin;

  useEffect(() => {
    const desired = HASH_BY_TAB[tab];
    if (desired && window.location.hash !== desired) {
      window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
    }
  }, [tab]);

  useEffect(() => {
    const onHashChange = () => {
      const next = TAB_BY_HASH[window.location.hash];
      if (typeof next === 'number') setTab(next);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, px: 3, pt: 2 }}>
        <BellIcon color="primary" />
        <Typography variant="h5" fontWeight={TYPE.semibold}>Notifications & Email</Typography>
        <PageHelpIcon pageKey="settings.notifications" />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_: SyntheticEvent, v: number) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Preferences" />
          {isAdmin && <Tab label="Templates" />}
          {isAdmin && <Tab label="Email Delivery" />}
        </Tabs>
      </Box>

      {/* Each sub-page manages its own layout/padding */}
      <TabPanel value={tab} index={0}>
        <NotificationPreferencesPage />
      </TabPanel>

      {isAdmin && (
        <TabPanel value={tab} index={1}>
          <NotificationTemplatesPage />
        </TabPanel>
      )}

      {isAdmin && (
        <TabPanel value={tab} index={2}>
          <EmailSettingsPage />
        </TabPanel>
      )}
    </Box>
  );
}
